<template>
  <div>
    <form
      id="plan-edit-form"
      @submit="checkForm"
      action="#"
      method="post"
      novalidate="true"
    >
      <h3 class="impact">PLAN</h3>
      <div v-if="user.userInfo.userType === 1" class="form-item">
        <label class="required" for="plan-name">{{
          $t("plan.planEditForm.planName")
        }}</label>
        <p v-if="errPlanName && planName.length > 30" class="error">
          {{ $t("plan.planEditForm.errorMsg.planName") }}
        </p>
        <p v-if="errSurrogatePair" class="error">
          {{ $t("plan.planEditForm.errorMsg.surrogatePair") }}
        </p>
        <input
          id="plan-name"
          name="plan-name"
          maxlength="30"
          v-model="planName"
          v-on:input="
            updateForm = true;
            invalidPlanName = false;
            errPlanName = false;
            errSurrogatePair = false;
          "
          v-bind:class="{
            error: errPlanName || errSurrogatePair || invalidPlanName,
          }"
        />
        <p v-if="errPlanName" class="error errorMsg">
          {{ $t("plan.planEditForm.errorMsg.noPlanName") }}
        </p>
        <p v-if="invalidPlanName" class="error errorMsg">
          {{ $t("plan.planEditForm.errorMsg.invalidPlanName") }}
        </p>
        <span
          v-bind:class="{ 'char-count': true, error: planName.length > 30 }"
        >
          {{ planName.length }} / 30
        </span>
      </div>

      <div class="form-item">
        <label class="required" for="plan-summary">{{
          $t("plan.planEditForm.planSummary")
        }}</label>
        <p v-if="errPlanSummary && planSummary.length > 300" class="error">
          {{ $t("plan.planEditForm.errorMsg.planSummary") }}
        </p>
        <textarea
          id="plan-summary"
          name="plan-summary"
          rows="4"
          maxlength="300"
          v-model="planSummary"
          v-on:input="
            updateForm = true;
            errPlanSummary = false;
          "
          v-bind:class="{ error: errPlanSummary }"
        ></textarea>
        <p v-if="errPlanSummary" class="error errorMsg">
          {{ $t("plan.planEditForm.errorMsg.noPlanSummary") }}
        </p>
        <span
          v-bind:class="{ 'char-count': true, error: planSummary.length > 300 }"
        >
          {{ planSummary.length }} / 300
        </span>
      </div>

      <div
        v-if="user.userInfo && user.userInfo.userType === 1"
        class="archive-wrapper"
      >
        <input
          type="checkbox"
          id="archive"
          v-model="planArchived"
          v-on:input="updateForm = true"
        />
        <label for="archive">{{ $t("plan.planEditForm.archive") }}</label>
      </div>

      <input
        type="submit"
        class="submit"
        v-bind:value="$t('plan.planEditForm.button.save')"
      />
    </form>
    <Modal
      v-if="showModal"
      v-bind:modalTitle="$t('plan.planEditForm.modal.header')"
      @close="showModal = false"
    >
      <div class="confirm-modal">
        <p>
          <strong>{{ $t("plan.planEditForm.modal.text") }}</strong>
        </p>
        <a v-on:click.prevent="updatePlan" class="button" href="#">OK</a>
        <a
          v-on:click="showModal = false"
          class="button archive-cancel"
          href="#"
          >{{ $t("plan.planEditForm.button.cancel") }}</a
        >
      </div>
    </Modal>
  </div>
</template>

<script>
import Modal from "@/components/Modal.vue";

import { validSurrogatePair } from "@/utils/validSurrogatePair";
import { validFileInclusion } from "@/utils/validFileInclusion";
import { planService } from "@/services/plan.service";
import { groupService } from "@/services/group.service";
import { projectService } from "@/services/project.service";
import { adminService } from "@/services/admin.service";

export default {
  name: "PlanEditForm",
  components: {
    Modal,
  },
  data() {
    return {
      user: {},
      plan: {},

      invalidForm: true,
      invalidPlanName: false,
      errPlanName: false,
      errSurrogatePair: false,
      errPlanSummary: false,

      planName: "",
      planSummary: "",
      planArchived: false,
      initPlanArchived: false,

      showModal: false,
      onceProc: true,
    };
  },
  created: function () {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.fetchPlan();
  },
  methods: {
    fetchPlan: function () {
      // grab the plan
      planService.getPlan(this.$route.params.planId).then((plan) => {
        this.plan = plan;
        this.planName = plan.name;
        if (plan.description) {
          this.planSummary = decodeURIComponent(atob(plan.description));
        }
        this.planArchived = !plan.enabled;
        this.initPlanArchived = !plan.enabled;
      });
    },
    checkForm: function (event) {
      this.invalidForm = false;
      this.errPlanName = false;
      this.errSurrogatePair = false;
      this.errPlanSummary = false;

      this.planName = this.planName.trim();
      this.planSummary = this.planSummary.trim();

      if (this.planName === "") {
        this.invalidForm = true;
        this.errPlanName = true;
      }

      if (this.planName.length > 30 || validFileInclusion(this.planName)) {
        this.invalidForm = true;
        this.invalidPlanName = true;
      }

      if (validSurrogatePair(this.planName)) {
        this.invalidForm = true;
        this.errSurrogatePair = true;
      }

      if (this.planSummary === "" || this.planSummary.length > 300) {
        this.invalidForm = true;
        this.errPlanSummary = true;
      }

      if (!this.invalidForm) {
        if (this.planArchived) {
          this.showModal = true;
        } else {
          this.updatePlan();
        }
      }

      event.preventDefault();
    },
    updatePlan: function () {
      let plan = {
        name: this.planName,
        description: btoa(encodeURIComponent(this.planSummary)),
        enabled: !this.planArchived,
      };

      // Sys admins should use the admin service.
      if (this.user.userInfo.userType === 1) {
        plan = {
          ...this.plan,
          ...plan,
        };
        adminService
          .planUpdate(this.plan.id, plan)
          .then(() => {
            this.updatePlanSuccess();
          })
          .catch((err) => {
            console.error("Unable to update plan: ", err);
          });
      }
      // Plan Admins use the plan service.
      else {
        planService
          .updatePlan(this.plan.id, plan)
          .then(() => {
            this.updatePlanSuccess();
          })
          .catch((err) => {
            console.error("Unable to update plan: ", err);
          });
      }
    },
    updatePlanSuccess: function () {
      // check to change Archive checkbox
      if (this.initPlanArchived != this.planArchived) {
        this.getGroups();
      } else {
        this.confirmArchive();
      }
    },
    getGroups: function () {
      planService
        .getGroups(this.plan.id)
        .then((groups) => {
          for (let group of groups) {
            this.updateGroup(group);
            this.getProjects(group);
          }
        })
        .catch((err) => {
          console.error("Something went wrong: ", err);
        });
    },
    getProjects: function (group) {
      groupService
        .getProjects(group.id)
        .then((projects) => {
          for (let project of projects) {
            this.updateProject(project);
          }
        })
        .catch((err) => {
          console.error("Something went wrong: ", err);
        });
    },
    updateGroup: function (group) {
      let modGroup = {
        description: group.description,
        name: group.name,
        enabled: !this.planArchived,
      };

      groupService
        .updateGroup(group.id, modGroup)
        .then(() => {})
        .catch((err) => {
          console.error("Something went wrong updating project: ", err);
        });
    },
    updateProject: function (project) {
      let modProject = {
        description: project.description,
        name: project.name,
        enabled: !this.planArchived,
      };

      projectService
        .updateProject(project.id, modProject)
        .then(() => {
          if (this.onceProc) {
            this.confirmArchive();
            this.onceProc = false;
          }
        })
        .catch((err) => {
          console.error("Something went wrong updating project: ", err);
        });
    },
    confirmArchive: function () {
      this.showModal = false;
      this.$router.push("/plan/" + this.plan.id);
    },
  },
};
</script>
<style lang="scss">
.modal-body {
  color: $text-dark;
  padding: 40px 40px 0;

  #plan-edit-form {
    padding: 20px 0;
  }
}

#plan-edit-form {
  max-width: 528px;

  .form-item {
    position: relative;
    .char-count {
      position: absolute;
      right: 0;
      bottom: 5px;
    }
  }

  .archive-wrapper {
    margin-bottom: 25px;

    label {
      display: inline;
    }
  }

  input.submit {
    @extend button;
    margin-top: 0;
    width: auto;
  }
  .auth-req {
    margin-top: 15px;
    padding-top: 30px;
    border-top: 2px solid #ccc;
  }
}

.confirm-modal {
  padding: 0 0 25px 0;
}

.archive-cancel {
  margin-left: 50px;
}
</style>
